<script setup>
import { ref, computed, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Textarea from 'primevue/textarea'
import Message from 'primevue/message'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'
import { useConversationsStore } from '@/stores/messages/conversations.js'
import messagesApi from '@/api/messagesApi.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  userId: { type: Number, required: false, default: null },
  responsibleName: { type: String, required: true },
  entityType: { type: String, default: 'facility' }, // 'facility' | 'contract' | 'work' | 'org'
  entityId: { type: [String, Number], required: true },
  entityTitle: { type: String, required: true },
  entityUrl: { type: String, required: true },
})
const emit = defineEmits(['update:modelValue', 'sent'])

const toast = useToast()
const conversations = useConversationsStore()

const visible = computed({ get: () => props.modelValue, set: v => emit('update:modelValue', v) })
const text = ref('')
const error = ref('')
const sending = ref(false)

const dialogTitle = computed(() => `Отправить сообщение ${props.responsibleName} по ${props.entityTitle}`)

watch(visible, (v) => { if (v) { text.value = ''; error.value = '' } })

function ruDativeByType(type) {
  const t = String(type || '').toLowerCase()
  switch (t) {
    case 'facility': return 'объекту'
    case 'contract': return 'договору'
    case 'work':     return 'работе'
    case 'org':      return 'организации'
    case 'customer': return 'клиенту'
    default:         return t || 'сущности'
  }
}

const humanContextTitle = computed(() => {
  return `По ${ruDativeByType(props.entityType)} ${props.entityTitle}`
})

async function submit() {
  error.value = ''
  const body = String(text.value || '').trim()
  if (!body) { error.value = 'Введите текст сообщения'; return }
  sending.value = true
  try {
    // 1) ensure-thread: создаём/находим тред по сущности
    const { conversation_id } = await messagesApi.ensureThread({
      user_id: props.userId,
      entity_type: props.entityType,
      entity_id: props.entityId,
      entity_title: humanContextTitle.value,
      entity_url: props.entityUrl,
    })

    // 2) отправка с тем же контекстом
    const context = {
      entity_type: props.entityType,
      entity_id: String(props.entityId),
      entity_title: humanContextTitle.value,
      entity_url: props.entityUrl,
    }
    const realMessage = await messagesApi.sendMessage(conversation_id, { body, ...context })

    // 3) локально поднимем карточку (сохранит липкий заголовок)
    conversations.upsertFromMessageEvent({ conversation_id, message: realMessage })

    toast.add({ severity: 'success', summary: 'Сообщение отправлено', life: 1800 })
    emit('sent', { conversation_id, message: realMessage })
    visible.value = false
  } catch (e) {
    const detail = e?.response?.data?.detail || e?.message || ''
    error.value = 'Не удалось отправить сообщение' + (detail ? `: ${detail}` : '')
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error.value, life: 3000 })
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <Dialog v-model:visible="visible" modal :style="{ width: '24rem', maxWidth: '95vw' }" :dismissableMask="true">
    <template #header>
      <div class="font-semibold">{{ dialogTitle }}</div>
    </template>

    <div class="flex flex-col gap-3">
      <Textarea v-model="text" rows="6" autoResize class="w-full" placeholder="Введите текст сообщения" style="resize:none" />
      <Message v-if="error" severity="error" size="small" variant="simple">{{ error }}</Message>
    </div>

    <template #footer>
      <div class="w-full flex justify-center gap-4 pt-2">
        <Button label="Отправить" :disabled="sending" severity="info" rounded @click="submit" />
        <Button label="Отмена" severity="secondary" rounded @click="visible = false" />
      </div>
    </template>
  </Dialog>
</template>